import React from 'react';
import { ShoppingCart, User, Heart, Search, BookOpen, Calendar, Share2, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template19 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-white font-serif">
            {/* Editorial Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template19')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            ARTISAN
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template19/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template19/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template19')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template19/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template19/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template19/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Magazine-style Hero */}
            <section className="relative">
                <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[70vh]">
                    {/* Hero Image */}
                    <div className="relative overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop"
                            alt="Editorial Fashion"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>

                    {/* Hero Content */}
                    <div className="bg-emerald-50 p-8 lg:p-16 flex items-center">
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <Calendar className="text-emerald-600" size={20} />
                                <span className="text-sm text-gray-600 tracking-widest">WINTER ISSUE 2025</span>
                            </div>
                            <h2 className="text-4xl lg:text-6xl font-bold text-emerald-800 mb-6 leading-tight">
                                The Future of Fashion
                            </h2>
                            <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8">
                                Explore the intersection of sustainability, innovation, and timeless style in our
                                comprehensive guide to conscious fashion choices.
                            </p>
                            <button className="bg-emerald-600 text-white px-8 py-4 hover:bg-emerald-700 transition-colors font-medium tracking-wide">
                                READ THE STORY
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Editorial Grid */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-5xl font-bold text-emerald-800 mb-4">Featured Stories</h2>
                        <div className="w-24 h-px bg-emerald-600 mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {[1, 2, 3].map(id => (
                            <article key={id} className="group">
                                <div className="aspect-[4/3] overflow-hidden mb-6">
                                    <img
                                        src={`https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&h=400&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${id * 23 + 600}`}
                                        alt={`Editorial Story ${id}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="mb-4">
                                    <span className="text-xs tracking-widest text-emerald-600 font-medium">
                                        {['FASHION', 'LIFESTYLE', 'CULTURE'][id - 1]}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-emerald-700 transition-colors">
                                    {['Sustainable Luxury in Modern Fashion', 'The Art of Conscious Dressing', 'Heritage Meets Innovation'][id - 1]}
                                </h3>
                                <p className="text-gray-600 leading-relaxed mb-4">
                                    Exploring the delicate balance between tradition and innovation in contemporary fashion design...
                                </p>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500">5 min read</span>
                                    <button className="text-emerald-600 hover:text-emerald-800 transition-colors font-medium">
                                        Read More →
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product Editorial */}
            <section className="bg-emerald-50 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-5xl font-bold text-emerald-800 mb-6">Editor's Picks</h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Curated selections from our editorial team featuring the season's most compelling pieces
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(id => (
                            <div key={id} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500">
                                <div className="aspect-[3/4] overflow-hidden relative">
                                    <img
                                        src={`https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=500&fit=crop&q=80&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=80&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${id * 27 + 700}`}
                                        alt={`Editor's Pick ${id}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute top-3 left-3">
                                        <span className="bg-emerald-600 text-white text-xs px-2 py-1 rounded font-medium">
                                            EDITOR'S CHOICE
                                        </span>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <h3 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">Featured Piece {id}</h3>
                                    <p className="text-lg font-bold text-emerald-700">${(129 + id * 25)}</p>
                                    <button className="w-full mt-3 border border-emerald-600 text-emerald-600 py-2 text-sm hover:bg-emerald-600 hover:text-white transition-colors font-medium">
                                        View Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="bg-emerald-800 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <Quote className="mx-auto mb-8 text-emerald-300" size={48} />
                    <blockquote className="text-2xl lg:text-4xl font-light text-white mb-8 max-w-4xl mx-auto leading-relaxed">
                        "Fashion is not just about clothing. It's about expressing who you are and who you want to become."
                    </blockquote>
                    <cite className="text-emerald-300 text-lg tracking-wide">— Editorial Director, BROADCAST</cite>
                </div>
            </section>

            {/* Newsletter Signup */}
            <section className="bg-white py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <h2 className="text-3xl lg:text-5xl font-bold text-emerald-800 mb-6">Stay Informed</h2>
                    <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                        Subscribe to receive our latest editorials, fashion insights, and exclusive content
                    </p>
                    <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            placeholder="Your email address"
                            className="flex-1 px-4 py-3 border border-gray-300 focus:border-emerald-600 focus:outline-none transition-colors"
                        />
                        <button className="bg-emerald-600 text-white px-8 py-3 hover:bg-emerald-700 transition-colors font-medium">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Editorial Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">ARTISAN</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template19')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template19/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template19/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template19/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@artisan.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 ARTISAN. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template19;